import { useState } from 'react';
import { setTheme } from '../theme.js';

// Single-button toggle that flips between light and dark. Mirrors the
// data-theme attribute set by the pre-render inline script so first
// paint is already correct — this just lets the user override it.
// We deliberately don't listen to prefers-color-scheme: TankSensei is
// dark-by-brand and the rest of the site (/, /demo, /admin) is
// dark-only. The toggle is a deliberate user opt-in to light, not
// something the OS can change for them.
export function ThemeToggle({ compact = false }) {
  const [mode, setMode] = useState(() => {
    if (typeof document === 'undefined') return 'dark';
    return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  });

  const flip = () => {
    const next = mode === 'light' ? 'dark' : 'light';
    setTheme(next);
    setMode(next);
  };

  const label = mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode';
  const icon =
    mode === 'light' ? (
      // Moon
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    ) : (
      // Sun
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );

  if (compact) {
    return (
      <button
        onClick={flip}
        aria-label={label}
        title={label}
        className="hdr-tap"
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          border: '1px solid var(--surface-border)',
          background: 'var(--surface)',
        }}
      >
        {icon}
      </button>
    );
  }

  return (
    <button
      onClick={flip}
      aria-label={label}
      className="font-heading"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minHeight: 44,
        padding: '8px 14px',
        borderRadius: 10,
        color: 'var(--text-muted)',
        border: '1px solid var(--surface-border)',
        fontSize: 12,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        textAlign: 'left',
      }}
    >
      {icon}
      <span>{mode === 'light' ? 'Dark mode' : 'Light mode'}</span>
    </button>
  );
}
